/**
 * CapabilityBadge - Displays a task's required capability as a color-coded badge
 *
 * Used in the LogicTree and ActiveResearchPanel to show what type of task
 * is being executed (reasoning, coding, math, creative, factual).
 */

import { Brain, Code, Calculator, Sparkles, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TaskCapability } from '@/types';
import { CAPABILITY_CONFIGS } from '@/types';

const CAPABILITY_ICONS = {
  reasoning: Brain,
  coding: Code,
  math: Calculator,
  creative: Sparkles,
  factual: Search,
};

interface CapabilityBadgeProps {
  capability: TaskCapability;
  className?: string;
  /** Show only icon without label */
  iconOnly?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
}

export function CapabilityBadge({
  capability,
  className,
  iconOnly = false,
  size = 'sm',
}: CapabilityBadgeProps) {
  const config = CAPABILITY_CONFIGS[capability];
  if (!config) return null;

  const Icon = CAPABILITY_ICONS[capability];

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md font-medium',
        config.color,
        config.bgColor,
        size === 'sm' ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-sm',
        className
      )}
      title={config.label}
    >
      <Icon className={cn(size === 'sm' ? 'w-3 h-3' : 'w-4 h-4')} />
      {!iconOnly && <span>{config.label}</span>}
    </span>
  );
}

export default CapabilityBadge;

